import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { pxToRem } from '~/theme/typography';
import { cell } from '~/components/Admin/ResourceTable/ResourceTable.style';
import { TableHeaderCell } from '~/components/Admin/TableHeaderCell';
import { TSort } from '~/types/common.type';
import { adminTab } from '~/types/enum';
import { StyledHoverTableRow, admin } from '~/views/Admin/Admin.style';
import { HEADERS } from '~/views/Admin/Admin.constant';
import { useGetRecordsQuery } from '~/api/oneBoxV3';
// import { TSearchCondition } from '~/store/resource/resource.type';
import {
  getterAdminRecordFilter,
  getterAdminRecordsPayload,
  getterRecords,
} from '~/store/record/recordGetter';
import { useAppDispatch } from '~/store';
import { setRecordSearchCondition } from '~/store/record';
import { getHeader } from '~/utils/constants';
import { endOfDate, startOfDate } from '~/utils/helper/format';
import { forceDownload } from '~/utils/helper/downloadFile';

type TProps = {
  sort: TSort<string>;
  handleSort: (property: string) => void;
  setTotalPageCount: (count: number) => void;
};

export const RecordTable = ({
  sort,
  handleSort,
  setTotalPageCount,
}: TProps) => {
  const dispatch = useAppDispatch();
  const { dateFrom, dateTo } = useSelector(getterAdminRecordFilter);
  const recordPayload = useSelector(getterAdminRecordsPayload);

  useGetRecordsQuery(recordPayload, { refetchOnMountOrArgChange: true });

  const { records, totalRecordsCount } = useSelector(state =>
    getterRecords(state, recordPayload),
  );

  useEffect(() => {
    setTotalPageCount(totalRecordsCount);
  }, [totalRecordsCount]);

  return (
    <>
      <admin.TopContainer>
        <Typography variant="h6">{adminTab.record}</Typography>
      </admin.TopContainer>
      <Box display="flex" alignItems="center" gap={2} py={1}>
        <DatePicker
          label={getHeader('dateFrom')}
          format="YYYY-MM-DD"
          value={dateFrom ? dayjs(dateFrom) : null}
          onChange={newValue => {
            newValue &&
              dispatch(
                setRecordSearchCondition({ dateFrom: startOfDate(newValue) }),
              );
          }}
          slotProps={{ textField: { size: 'small' } }}
          disableFuture
        />
        <DatePicker
          label={getHeader('dateTo')}
          format="YYYY-MM-DD"
          value={dateTo ? dayjs(dateTo) : null}
          onChange={newValue => {
            newValue &&
              dispatch(
                setRecordSearchCondition({ dateTo: endOfDate(newValue) }),
              );
          }}
          slotProps={{ textField: { size: 'small' } }}
          disableFuture
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            {HEADERS.record.map(header => (
              <TableHeaderCell
                key={header.title}
                data={header}
                sort={sort}
                handleSort={handleSort}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map(item => {
            const { _id, recordTime, url } = item;
            const fileName = dayjs(recordTime).format('YYYY/MM/DD HH:mm');
            return (
              <StyledHoverTableRow
                key={_id}
                sx={{
                  '&.MuiTableRow-hover:hover': {
                    backgroundColor: 'info.lighter',
                  },
                }}
              >
                <cell.Container
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: pxToRem(16),
                      fontWeight: '500',
                    }}
                  >
                    <Description
                      sx={{
                        fill: 'grey.600',
                        fontSize: pxToRem(20),
                      }}
                    />
                    {fileName} 歷程紀錄
                  </Typography>

                  <Button
                    variant="contained"
                    color="info"
                    sx={{
                      '&:hover': { backgroundColor: 'info.dark' },
                    }}
                    onClick={() => forceDownload({ url, name: fileName })}
                  >
                    檔案下載
                  </Button>
                </cell.Container>
              </StyledHoverTableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};
